import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Notification } from '@/types';

interface NotificationsResponse extends PaginatedData<Notification> {
  unreadCount: number;
}

// Get notifications list
export function useNotifications(page: number = 1, perPage: number = 20, unreadOnly: boolean = false) {
  return useQuery({
    queryKey: ['notifications', page, perPage, unreadOnly],
    queryFn: async (): Promise<NotificationsResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });
      if (unreadOnly) {
        params.append('unread', 'true');
      }
      const response = await api.get<ApiResponse<NotificationsResponse>>(`/notifications?${params}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch notifications');
      }
      return response.data.data;
    },
  });
}

// Mark notification as read
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.patch<ApiResponse>(`/notifications/${id}/read`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to mark as read');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Mark all notifications as read
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<ApiResponse>('/notifications/read-all');
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to mark all as read');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Delete notification
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete<ApiResponse>(`/notifications/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete notification');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
